import type React from "react";
import {Fragment} from "react";
import {Box, Link, Stack, Typography} from "@mui/material";
import {CustomCard} from "~/components/Card/Card";
import {Tag} from "~/components/tag/Tag";
import type {SupplierRiskSignal} from "~/pages/supplier-360/types/Supplier360Types";
import AutoCatIcon from '~/icons/svg/AutoCategorizationIcon.svg?react'
import {formatSignalDate, IMPACT_TYPE_COLOR, toneColor} from "~/pages/supplier-360/tabs/SupplierInsights/signalFeed";
import {MONO_FONT} from "~/style/fonts";

/** A dot-separated subheader item — `Tag` for the language code, plain text otherwise. */
type SubheaderPart = {key: string, content: string, asTag?: boolean};

/**
 * One `risk_signal` row in the Insights tab's signal feed — the design's feed `sx-card`. Rendered
 * per row as the API returns it: an article tagged with two sub-categories arrives as two rows.
 *
 * Several fields (`category`/`impact_type`/`language`/`relevance_score`/`intensity`) only ever carry
 * a real value from SV's own classification — an Apify-sourced row (news/website/LinkedIn) leaves
 * them blank/`0` rather than omitting them, since the model has no per-source optional-field
 * concept. Each is hidden here rather than shown as an empty tag or "intensity 0" placeholder.
 */
export const SignalFeedCard: React.FC<{article: SupplierRiskSignal}> = ({article}) => {
    const subheaderParts: (SubheaderPart | null)[] = [
        article.source_name ? {key: 'source', content: article.source_name} : null,
        formatSignalDate(article.published_at) ? {key: 'date', content: formatSignalDate(article.published_at)} : null,
        article.language ? {key: 'language', content: article.language, asTag: true} : null,
    ];
    const visibleSubheaderParts = subheaderParts.filter((part): part is SubheaderPart => part !== null);

    const hasBodyContent = Boolean(article.impact_type || article.category || article.event_type || article.intensity);

    return (
        <CustomCard
            title={article.source_url
                ? <Link href={article.source_url} target="_blank" rel="noopener noreferrer" underline="hover" color="inherit" sx={{'&:hover': {color: 'info.main'}}}>{article.title}</Link>
                : article.title}
            subHeader={visibleSubheaderParts.length > 0 && (
                <Stack direction="row" alignItems="center" gap={0.75}>
                    {visibleSubheaderParts.map((part, i) => (
                        <Fragment key={part.key}>
                            {i > 0 && <Box sx={{height: '2px', width: '2px', borderRadius: '50%', bgcolor: 'text.secondary'}}/>}
                            {part.asTag
                                ? <Tag label={part.content} color="default" variant="outlined" sx={{minHeight: 19, borderRadius: '4px !important'}}/>
                                : <Typography variant="caption" color="text.secondary">{part.content}</Typography>}
                        </Fragment>
                    ))}
                </Stack>
            )}
            status={article.sentiment || undefined}
            statusColor={toneColor(article.tone)}
            extraHeaderContent={article.relevance_score > 0 && (
                <Tag label={Math.round(article.relevance_score)} color="default" variant="outlined" sx={{width: 46}}/>
            )}
        >
            {hasBodyContent && (
                <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap" sx={{mt: 1}}>
                    {article.impact_type && (
                        <Tag label={article.impact_type} color={IMPACT_TYPE_COLOR[article.impact_type] ?? 'default'} solid={false}/>
                    )}
                    {article.category && <Tag label={article.category} color="default" variant="outlined"/>}
                    {article.event_type && (
                        <Tag
                            label={article.event_type}
                            color="default"
                            variant="outlined"
                            icon={<AutoCatIcon width={14} height={14} />}
                        />
                    )}
                    {article.intensity > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{font: MONO_FONT}}>
                            {`intensity ${article.intensity.toLocaleString('en-GB')}`}
                        </Typography>
                    )}
                </Stack>
            )}
        </CustomCard>
    );
};
